import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchDocs, type SearchHit } from '../../lib/docs';
import { Icon } from '../Icon';

interface DocsSearchProps {
    onNavigate?: () => void;
}

export function DocsSearch({ onNavigate }: DocsSearchProps) {
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState<SearchHit[]>([]);
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const navigate = useNavigate();
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setHits(searchDocs(query, 6));
        setActiveIdx(0);
    }, [query]);

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    // Raccourci "/" pour focus
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            const inField =
                target &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable);
            if (!inField && e.key === '/') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const goTo = (hit: SearchHit) => {
        navigate(hit.page.path);
        setOpen(false);
        setQuery('');
        onNavigate?.();
    };

    const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, hits.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && hits[activeIdx]) {
            e.preventDefault();
            goTo(hits[activeIdx]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className="docs-search" ref={ref}>
            <div className="docs-search-input-wrap">
                <Icon name="search" size={15} />
                <input
                    ref={inputRef}
                    type="search"
                    placeholder="Rechercher… (appuyez sur / )"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKey}
                    aria-label="Rechercher dans la documentation"
                />
            </div>

            {open && query.trim().length >= 2 && (
                <div className="docs-search-results" role="listbox">
                    {hits.length === 0 ? (
                        <div className="docs-search-empty">Aucun résultat.</div>
                    ) : (
                        hits.map((hit, i) => (
                            <button
                                type="button"
                                key={hit.page.path}
                                className={`docs-search-hit${i === activeIdx ? ' is-active' : ''}`}
                                onMouseEnter={() => setActiveIdx(i)}
                                onClick={() => goTo(hit)}
                                role="option"
                                aria-selected={i === activeIdx}
                            >
                                <div className="docs-search-hit-title">
                                    {hit.page.frontmatter.title}
                                </div>
                                <div className="docs-search-hit-path">
                                    {hit.page.section} / {hit.page.slug}
                                </div>
                                <div className="docs-search-hit-snippet">{hit.snippet}</div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
